import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

// Web3Forms access key
const WEB3FORMS_KEY = '8e10698e-fba8-4a67-82e3-89b46d0a0c1c';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

const engagementModels = [
  {
    title: 'Advisory',
    description: 'Strategic consulting and transformation planning supported by legal and regulatory advisory.',
  },
  {
    title: 'Transformation Programs',
    description: 'Project-based engagements focused on delivering end-to-end transformation initiatives.',
  },
  {
    title: 'Managed Governance',
    description: 'Ongoing oversight of operational performance and transformation initiatives.',
  },
  {
    title: 'Retainer Model',
    description: 'Continuous access to SGA expertise and advisory services.',
  },
];

export const EngagePage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Check for success redirect from Web3Forms
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setSubmitStatus('success');
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrorMessage('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setErrorMessage('Please enter your name');
      setSubmitStatus('error');
      return false;
    }
    if (!formData.email.trim()) {
      setErrorMessage('Please enter your email');
      setSubmitStatus('error');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      setSubmitStatus('error');
      return false;
    }
    if (!formData.message.trim()) {
      setErrorMessage('Please enter a message');
      setSubmitStatus('error');
      return false;
    }
    if (formData.message.trim().length < 10) {
      setErrorMessage('Message must be at least 10 characters');
      setSubmitStatus('error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataObj = new FormData();
      formDataObj.append('access_key', WEB3FORMS_KEY);
      formDataObj.append('subject', `SGA STRAT Website - New Inquiry from ${formData.name}`);
      formDataObj.append('from_name', 'SGA STRAT Website');
      formDataObj.append('name', formData.name);
      formDataObj.append('email', formData.email);
      formDataObj.append('organization', formData.organization || 'Not provided');
      formDataObj.append('message', formData.message);
      formDataObj.append('replyto', formData.email);
      
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataObj
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          organization: '',
          email: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.message || 'Failed to submit form. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Failed to submit form. Please try again later.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="engage-page">
      {/* Hero Banner - 1920 × 420px slim banner */}
      <section 
        className="relative flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3)), url('/assets/banners/Engage_SGA_Banner.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          minHeight: '420px',
          height: '420px',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 w-full">
          <motion.div 
            className="max-w-lg"
            initial="initial"
            animate="animate"
            variants={{
              animate: { transition: { staggerChildren: 0.1 } }
            }}
          >
            <motion.h1 
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="font-montserrat font-bold text-3xl md:text-4xl text-white mb-4"
            >
              Start Your Transformation Journey
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-inter text-base text-white leading-relaxed"
            >
              Engage with our experts to design, execute and translate strategy 
              into measurable outcomes.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
              How We Work
            </span>
            <h2 className="heading-section text-[#0B1E2D] mb-6">
              Partner with SGA STRAT
            </h2>
            <p className="body-text max-w-3xl">
              SGA STRAT offers flexible engagement models designed to meet the needs 
              of organizations at different stages of transformation.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {engagementModels.map((model, index) => (
              <motion.div
                key={model.title}
                data-testid={`engagement-model-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-50 p-6 border-l-4 border-[#0087B4] hover:bg-white hover:shadow-lg transition-all duration-300"
              >
                <h3 className="font-montserrat font-semibold text-lg text-[#0B1E2D] mb-3">
                  {model.title}
                </h3>
                <p className="font-inter text-sm text-slate-500">
                  {model.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
                Get in Touch
              </span>
              <h2 className="font-montserrat font-bold text-2xl md:text-3xl text-[#0B1E2D] mb-8">
                Engage with SGA
              </h2>

              {submitStatus === 'success' ? (
                <div className="bg-green-50 border border-green-200 p-8 text-center" data-testid="success-message">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                  <h3 className="font-montserrat font-semibold text-xl text-green-800 mb-2">
                    Thank You!
                  </h3>
                  <p className="font-inter text-green-600">
                    Your message has been received. We will get back to you shortly.
                  </p>
                </div>
              ) : (
                <form 
                  action="https://api.web3forms.com/submit" 
                  method="POST"
                  onSubmit={(e) => {
                    if (!validateForm()) {
                      e.preventDefault();
                      return;
                    }
                    setIsSubmitting(true);
                  }}
                  className="space-y-6" 
                  data-testid="contact-form"
                >
                  <input type="hidden" name="access_key" value={WEB3FORMS_KEY} />
                  <input type="hidden" name="subject" value="SGA STRAT Website - New Contact Inquiry" />
                  <input type="hidden" name="from_name" value="SGA STRAT Website" />
                  <input type="hidden" name="redirect" value="https://sgastrat.com/engage?success=true" />
                  
                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 p-4 flex items-start gap-3" data-testid="error-message">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="font-inter text-sm text-red-600">{errorMessage}</p>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="name" className="font-inter font-medium text-[#0B1E2D] mb-2 block">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      className="border-slate-200 focus:border-[#0087B4] focus:ring-[#0087B4]"
                      data-testid="input-name"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <Label htmlFor="organization" className="font-inter font-medium text-[#0B1E2D] mb-2 block">
                      Organization
                    </Label>
                    <Input
                      id="organization"
                      name="organization"
                      type="text"
                      value={formData.organization}
                      onChange={handleInputChange}
                      placeholder="Your company or organization"
                      className="border-slate-200 focus:border-[#0087B4] focus:ring-[#0087B4]"
                      data-testid="input-organization"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="font-inter font-medium text-[#0B1E2D] mb-2 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      className="border-slate-200 focus:border-[#0087B4] focus:ring-[#0087B4]"
                      data-testid="input-email"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-inter font-medium text-[#0B1E2D] mb-2 block">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your transformation needs..."
                      rows={5}
                      className="border-slate-200 focus:border-[#0087B4] focus:ring-[#0087B4] resize-none"
                      data-testid="input-message"
                      disabled={isSubmitting}
                    />
                    <p className="font-inter text-xs text-slate-400 mt-1">
                      {formData.message.length}/5000 characters
                    </p>
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-button"
                    disabled={isSubmitting}
                    className="w-full bg-[#0087B4] hover:bg-[#006A8E] text-white rounded-none py-6 font-montserrat font-bold tracking-wider"
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        Submit
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:pl-8"
            >
              <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#4DB848] mb-4 block">
                Contact Information
              </span>
              <h2 className="font-montserrat font-bold text-2xl md:text-3xl text-[#0B1E2D] mb-8">
                Let's Connect
              </h2>

              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <div className="w-12 h-12 bg-[#4DB848]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#4DB848]" />
                  </div>
                  <div>
                    <h4 className="font-montserrat font-semibold text-[#0B1E2D] mb-1">
                      Office Address
                    </h4>
                    <p className="font-inter text-slate-500">
                      Jasmine Street, Kammanahalli, Bengaluru - 560084, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-email">
                  <div className="w-12 h-12 bg-[#4DB848]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#4DB848]" />
                  </div>
                  <div>
                    <h4 className="font-montserrat font-semibold text-[#0B1E2D] mb-1">
                      Email
                    </h4>
                    <p className="font-inter text-slate-500">
                      shekhar@sgastrat.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-[#4DB848]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#4DB848]" />
                  </div>
                  <div>
                    <h4 className="font-montserrat font-semibold text-[#0B1E2D] mb-1">
                      Phone
                    </h4>
                    <p className="font-inter text-slate-500">
                      +91 80970 48355
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div 
                className="aspect-video overflow-hidden border border-slate-200"
                data-testid="map-container"
              >
                <iframe
                  src="https://www.google.com/maps?q=Jasmine+Street,+Kammanahalli,+Bengaluru+560084,+India&output=embed&z=17"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SGA STRAT Office Location - Jasmine Street, Kammanahalli, Bengaluru"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
